import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import {
  Spinner,
  FullPageSpinner,
  ComponentFailure,
  TimeoutModal,
  NoTableResults,
} from 'components'
import { NotificationsAgreementsTable } from '../components'
import { sanitizeForSearch, parseDateFromString, useSearchParams } from 'utils'
import { selectors } from '../reducer'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as apiActions from 'api-actions'
import { useLocation, useHistory } from 'react-router-dom'
import { castArray, some } from 'lodash'
import {
  BDActionItems,
  Path,
  FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
} from 'config/portal'
import {
  NOTIFICATION_SERVICES_ADDENDUM_SUCCESS_MESSAGE,
  NOTIFICATION_SERVICES_ORDER_SUCCESS_MESSAGE,
} from 'config/notifications'
import { ProductTypeTitles } from 'config/products'
import * as actions from '../actions'
import { startOfDay } from 'date-fns'
import { flashSuccessMessage } from 'redux-flash'
import { Icon as PlusIcon } from 'images/plus.svg'
import { ServicesAgreementStatus } from 'config/automatic-rollovers'

const propTypes = {
  notificationsPlanAgreements: PropTypes.arrayOf(
    Types.notificationsPlanAgreementsSummary
  ),
  fetchNotificationsPlanAgreements: PropTypes.func.isRequired,
  shouldTriggerAllNotificationsPlanAgreementsFetch: PropTypes.bool.isRequired,
  setShouldTriggerAllNotificationsPlanAgreementsFetch:
    PropTypes.func.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
}

const defaultProps = {
  servicesAgreements: null,
}

function ServicesAgreementsHeader({ children }) {
  return (
    <div className="header-bar">
      <div className="header-bar-content">
        <div className="header-bar-page-title">
          <h1>{ProductTypeTitles.NOTIFICATIONS}</h1>
          <p>
            Ensure the plan has an automatic rollover services agreement and
            notification services addendum on file before starting a
            notification order.
          </p>
        </div>
        {children}
      </div>
    </div>
  )
}

function TableComponentFailure() {
  return (
    <div className="content-wrapper all-plans">
      <ServicesAgreementsHeader />
      <div className="services-agreements-table-wrapper">
        <ComponentFailure />
      </div>
    </div>
  )
}

function NotificationsPlanAgreements({
  notificationsPlanAgreements,
  fetchNotificationsPlanAgreements,
  shouldTriggerAllNotificationsPlanAgreementsFetch,
  setShouldTriggerAllNotificationsPlanAgreementsFetch,
  flashSuccessMessageHandler,
}) {
  const location = useLocation()
  const history = useHistory()
  const searchParams = useSearchParams()
  const noNotificationsPlanAgreements = !notificationsPlanAgreements?.length
  const hasCompletedAddendum = some(
    notificationsPlanAgreements,
    (notification) =>
      notification.addendumStatus === ServicesAgreementStatus.COMPLETED ||
      notification.addendumStatus === ServicesAgreementStatus.COMBINED_COMPLETED
  )

  useEffect(() => {
    window.appEventData.pop()
    var appEventData = window.appEventData || []
    appEventData.push({
      event: 'Page Load Completed',
    })
  }, [])
  const [error, setError] = useState(false)
  const [showTimeoutModal, setShowTimeoutModal] = useState(
    !!location.state?.showTimeoutModal
  )
  const [
    showNotificationsAddendumSuccessModal,
    setShowNotificationsAddendumSuccessModal,
  ] = useState(!!location.state?.showNotificationsAddendumSuccessModal)
  const [
    showNotificationsOrderSuccessModal,
    setShowNotificationsOrderSuccessModal,
  ] = useState(!!location.state?.showNotificationsOrderSuccessModal)

  useEffect(() => {
    async function getNotificationsPlanAgreements() {
      try {
        await fetchNotificationsPlanAgreements()
        setShouldTriggerAllNotificationsPlanAgreementsFetch(false)
      } catch {
        setError(true)
      }
    }
    if (shouldTriggerAllNotificationsPlanAgreementsFetch)
      getNotificationsPlanAgreements()
  }, [shouldTriggerAllNotificationsPlanAgreementsFetch])

  useEffect(() => {
    if (showNotificationsAddendumSuccessModal) {
      flashSuccessMessageHandler(
        NOTIFICATION_SERVICES_ADDENDUM_SUCCESS_MESSAGE,
        {
          timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
        }
      )
      setShowNotificationsAddendumSuccessModal(false)
    }
  }, [
    showNotificationsAddendumSuccessModal,
    flashSuccessMessageHandler,
    setShowNotificationsAddendumSuccessModal,
  ])

  useEffect(() => {
    if (showNotificationsOrderSuccessModal) {
      flashSuccessMessageHandler(NOTIFICATION_SERVICES_ORDER_SUCCESS_MESSAGE, {
        timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
      })
      setShowNotificationsOrderSuccessModal(false)
    }
  }, [
    showNotificationsOrderSuccessModal,
    flashSuccessMessageHandler,
    setShowNotificationsOrderSuccessModal,
  ])

  const filters = useMemo(() => {
    let initialFilters = {
      agreementStatus: [],
      initialInvestment: [],
      planSponsor: [],
      startLastUpdatedDate: '',
      endLastUpdatedDate: '',
    }
    const {
      agreementStatus,
      initialInvestment,
      planSponsor,
      startLastUpdatedDate,
      endLastUpdatedDate,
    } = searchParams

    if (agreementStatus)
      initialFilters.agreementStatus = castArray(agreementStatus)
    if (initialInvestment)
      initialFilters.initialInvestment = castArray(initialInvestment)
    if (planSponsor) initialFilters.planSponsor = castArray(planSponsor)
    if (startLastUpdatedDate)
      initialFilters.startLastUpdatedDate = startOfDay(
        parseDateFromString(startLastUpdatedDate)
      )
    if (endLastUpdatedDate)
      initialFilters.endLastUpdatedDate = startOfDay(
        parseDateFromString(endLastUpdatedDate)
      )

    return initialFilters
  }, [searchParams])

  const searchValue = searchParams.q ?? ''

  // User can search services agreements by plan name, sponsor name, and plan EIN
  const filteredNotificationsPlanAgreements = useMemo(() => {
    if (!notificationsPlanAgreements) return

    const filteredNotificationsPlanAgreements =
      notificationsPlanAgreements.filter(
        ({ planSponsorName, addendumStatus }) => {
          const { agreementStatus, planSponsor } = filters
          return (
            (agreementStatus.length
              ? agreementStatus.includes(addendumStatus)
              : true) &&
            (planSponsor.length ? planSponsor.includes(planSponsorName) : true)
          )
        }
      )

    // ignore hyphens and case
    const sanitizedValue = sanitizeForSearch(searchValue)
    if (sanitizedValue === '') return filteredNotificationsPlanAgreements

    return filteredNotificationsPlanAgreements.filter(
      ({ planSponsorName, ein, completeLegalPlanName }) =>
        sanitizeForSearch(completeLegalPlanName).includes(sanitizedValue) ||
        sanitizeForSearch(planSponsorName).includes(sanitizedValue) ||
        (ein && sanitizeForSearch(ein).includes(sanitizedValue))
    )
  }, [filters, searchValue, notificationsPlanAgreements])

  const isLoading = !notificationsPlanAgreements && !error

  if (isLoading) return <FullPageSpinner />
  if (error) return <TableComponentFailure />

  const handleAddNewNotificationsAddendumAction = () =>
    history.push({
      pathname: Path.ADD_NEW_NOTIFICATIONS_ADDENDUM,
      state: {
        ignorePrompt: true,
      },
    })

  /**
   * @description Handles onClick action for when the user clicks on ***Initiate Rollover*** or ***Initiate Distribution*** button
   */
  const handleInitiateParticipantUploadAction = () =>
    history.push({
      pathname: Path.UPLOAD_FILE_NOTIFICATION_SERVICES,
      state: {
        hasConfirmed: true,
        search: location.search,
      },
    })

  // const notificationFieldMappings = [
  //   { field: 'name', displayName: 'Complete Legal Plan Name' },
  //   { field: 'planEIN', displayName: 'EIN' },
  //   { field: 'planSponsorCompany.name', displayName: 'Plan Sponsor Name' },
  // ]

  // const planFiduciaryFieldMappings = notificationFieldMappings.slice(2)

  // const agreementFieldMappings =
  //   currentUser.accountAudience === 'Plan Fiduciary'
  //     ? planFiduciaryFieldMappings
  //     : notificationFieldMappings

  const notificationsAddendumActions = () => {
    return (
      <>
        <button
          type="button"
          onClick={handleAddNewNotificationsAddendumAction}
          className="button-text"
        >
          <PlusIcon aria-hidden="true" />
          {BDActionItems.CREATE_NEW_ADDENDUM}
        </button>

        {hasCompletedAddendum && (
          <button
            type="button"
            onClick={() => handleInitiateParticipantUploadAction()}
            className="button-primary"
          >
            {'Start notification order'}
          </button>
        )}
      </>
    )
  }

  const productActionItems = () => {
    return notificationsAddendumActions()
  }

  return (
    <div className="content-wrapper all-plans">
      <ServicesAgreementsHeader>
        <div className="services-agreements-actions">
          {productActionItems()}
        </div>
      </ServicesAgreementsHeader>
      {shouldTriggerAllNotificationsPlanAgreementsFetch && <Spinner />}
      <NotificationsAgreementsTable
        // agreementsType={agreementsType}
        filters={filters}
        tableId="services-agreements-table"
        notificationsPlanAgreements={filteredNotificationsPlanAgreements}
        searchValue={searchValue}
        //noNotificationsPlanAgreements={noNotificationsPlanAgreements}
        // fieldMappings={agreementFieldMappings}
        noRowsFoundMessage={
          <NoTableResults
            title={
              noNotificationsPlanAgreements
                ? 'You don’t have any addendums at this time.'
                : 'No results found.'
            }
            titleAs="h2"
            details={
              noNotificationsPlanAgreements
                ? 'Need to create a new addendum? You can do that now.'
                : 'Try adjusting your filter or search.'
            }
          >
            {noNotificationsPlanAgreements && (
              <button
                type="button"
                className="button-primary"
                onClick={handleAddNewNotificationsAddendumAction}
              >
                {BDActionItems.CREATE_NEW_ADDENDUM}
              </button>
            )}
          </NoTableResults>
        }
      />
      {showTimeoutModal && (
        <TimeoutModal onClose={() => setShowTimeoutModal(false)} />
      )}
    </div>
  )
}

function mapStateToProps(state) {
  return {
    notificationsPlanAgreements: selectors.notificationsPlanAgreements(state),
    shouldTriggerAllNotificationsPlanAgreementsFetch:
      selectors.shouldTriggerAllNotificationsPlanAgreementsFetch(state),
  }
}

const mapDispatchToProps = {
  fetchNotificationsPlanAgreements: apiActions.fetchNotificationsPlanAgreements,
  setShouldTriggerAllNotificationsPlanAgreementsFetch:
    actions.setShouldTriggerAllNotificationsPlanAgreementsFetch,
  flashSuccessMessageHandler: flashSuccessMessage,
}

NotificationsPlanAgreements.propTypes = propTypes
NotificationsPlanAgreements.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  NotificationsPlanAgreements
)
